import React, { useMemo, useState, useEffect } from 'react'

import { useModal } from '@/contexts/ModalContext'

import { ENDPOINTS, sendPostRequest } from '@/services/api'
import { onUncaughtError } from '@/services/userAlerts'

import { useTranslation } from 'react-i18next'
import { i18n } from '@/translate/i18n'
import Swal from 'sweetalert2'

import * as S from './styles'
import CustomSelect from './CustomSelect'
import { useContentTree } from '@/contexts/ContentTreeContext'
import { getWithExpiry, LOCAL_KEYS } from '@/services/browserStorage'
import type { Tree } from '@/types/contentTree'

export interface ContentSuggestionModalConfig {
  placeholder?: never
}

export interface ContentSuggestionModalProps {
  config: ContentSuggestionModalConfig
}

const ContentSuggestionModal = ({}: ContentSuggestionModalProps) => {
  const { t } = useTranslation()
  const { closeModal } = useModal()
  const { tree: contextTree } = useContentTree()

  const [loading, setLoading] = useState(false)
  const [moduleId, setModuleId] = useState('')
  const [title, setTitle] = useState('')
  const [details, setDetails] = useState('')
  const [tree, setTree] = useState<Tree>([])

  useEffect(() => {
    if (contextTree && contextTree.length > 0) {
      setTree(contextTree)
      return
    }

    const cachedTree = getWithExpiry<Tree>(LOCAL_KEYS.content_tree)
    if (cachedTree) {
      setTree(cachedTree)
    }
  }, [contextTree])

  const modules = useMemo(
    () =>
      tree
        ?.filter(mod => mod.name !== 'Simulador' && mod.name !== 'Simulator')
        .map(mod => ({ id: mod.name, name: mod.name })) || [],
    [tree]
  )
  const isFormValid = moduleId !== '' && title.trim() !== ''

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!isFormValid) return

    setLoading(true)

    const body: {
      module: string
      title: string
      description?: string
    } = {
      module: moduleId,
      title: title.trim()
    }

    if (details.trim()) {
      body.description = details.trim()
    }

    try {
      const response = await sendPostRequest(ENDPOINTS.suggestions, body)

      if (response && response.status >= 200 && response.status < 300) {
        closeModal()
        Swal.fire({
          icon: 'success',
          title: i18n.t('swal.success'),
          html: t('contactPage.contentSuggestion.successMessage')
        })
      } else {
        onUncaughtError()
      }
    } catch (error) {
      onUncaughtError()
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setModuleId('')
    setTitle('')
    setDetails('')
    closeModal()
  }

  return (
    <S.ContentSuggestionModal>
      <S.ModalHeader>
        <p>{t('contactPage.contentSuggestion.title')}</p>
        <button
          id="btn_closemodal_suggestion"
          type="button"
          onClick={handleClose}
        >
          <span className="material-icons">close</span>
        </button>
      </S.ModalHeader>

      <form onSubmit={handleSubmit}>
        <S.FormGroup>
          <label htmlFor="module">
            {t('contactPage.contentSuggestion.moduleLabel')}{' '}
            <span className="required">*</span>
          </label>
          <CustomSelect
            id="module"
            value={moduleId}
            onChange={setModuleId}
            options={modules}
            placeholder={t('contactPage.contentSuggestion.modulePlaceholder')}
            required
          />
        </S.FormGroup>

        <S.FormGroup>
          <label htmlFor="title">
            {t('contactPage.contentSuggestion.titleLabel')}{' '}
            <span className="required">*</span>
          </label>
          <S.Input
            id="title"
            type="text"
            placeholder={t('contactPage.contentSuggestion.titlePlaceholder')}
            value={title}
            onChange={e => setTitle(e.target.value)}
            maxLength={100}
            required
          />
        </S.FormGroup>

        <S.FormGroup>
          <label htmlFor="details">
            {t('contactPage.contentSuggestion.detailsLabel')}
          </label>
          <S.Textarea
            id="details"
            placeholder={t('contactPage.contentSuggestion.detailsPlaceholder')}
            value={details}
            onChange={e => setDetails(e.target.value)}
            maxLength={500}
            rows={4}
          />
          <S.CharCount>{details.length}/500</S.CharCount>
        </S.FormGroup>

        <S.ButtonGroup>
          <S.SubmitButton
            type="submit"
            disabled={!isFormValid || loading}
            $isValid={isFormValid}
          >
            {loading
              ? t('contactPage.contentSuggestion.submitting')
              : t('contactPage.contentSuggestion.submit')}
          </S.SubmitButton>
        </S.ButtonGroup>
      </form>
    </S.ContentSuggestionModal>
  )
}

export default ContentSuggestionModal
